import {Injectable, OnDestroy} from '@angular/core';
import {Classifications} from '@docma-mm-core/classification/classification';
import {Contact} from '@docma-mm-core/contact/contact';
import {Craft} from '@docma-mm-core/craft/craft';
import {FieldMinimal} from '@docma-mm-core/field/field';
import {Layout} from '@docma-mm-core/layout/layout';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmModel} from '@docma-mm-core/model/mm.model';
import {Order} from '@docma-mm-core/order/order';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {Status} from '@docma-mm-core/status/status';
import {flattenStructure, Structure} from '@docma-mm-core/structure/structure';
import {UserDefineds} from '@docma-mm-core/user-defined/user-defined';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {ConnectivityService} from '@mydocma/core';
import {Project as MydocmaProject} from '@mydocma/project';
import {BehaviorSubject, defer, firstValueFrom, map, Observable, switchMap} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {MmProjectOfflineService} from './mm-project-offline.service';
import {DocumentType} from '@docma-mm-core/document-type/document-type';
import {Company} from '@docma-mm-core/company/company';
import {PlanData} from '@docma-mm-core/plan/plan';
import {ProjectSetting} from '@docma-mm-core/project-settings/project-setting';
import {ProjectUserPermissions} from '@docma-mm-core/permission/project-user-permissions';
import {Attribute} from '@docma-mm-core/attribute/attribute';
import {AttributeGroup} from '@docma-mm-core/attribute-group/attribute-group';
import {AttributeGroupFile} from '@docma-mm-core/attribute-group/attribute-group-file';
import {ProjectSettingEnum} from '@docma-mm-core/project/configuration/project-setting.enum';
import {Delay} from '@docma-mm-core/delay/delay';

@Injectable()
export class MmProjectLoaderService implements OnDestroy {

  private projectSubject = new BehaviorSubject<MmModel<MmProject>>((new MmModel<MmProject>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private filedsSubject = new BehaviorSubject<MmModel<FieldMinimal[]>>((new MmModel<FieldMinimal[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private layoutsSubject = new BehaviorSubject<MmModel<Layout[]>>((new MmModel<Layout[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private structuresSubject = new BehaviorSubject<MmModel<Structure[]>>((new MmModel<Structure[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private ordersSubject = new BehaviorSubject<MmModel<Order[]>>((new MmModel<Order[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private craftsSubject = new BehaviorSubject<MmModel<Craft[]>>((new MmModel<Craft[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private contactsSubject = new BehaviorSubject<MmModel<Contact[]>>((new MmModel<Contact[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  userDefinedsSubject = new BehaviorSubject<MmModel<UserDefineds>>(new MmModel<UserDefineds>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  }));
  private classificationsSubject = new BehaviorSubject<MmModel<Classifications>>((new MmModel<Classifications>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private statusesSubject = new BehaviorSubject<MmModel<Status[]>>((new MmModel<Status[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private documentTypesSubject = new BehaviorSubject<MmModel<DocumentType[]>>((new MmModel<DocumentType[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private companiesSubject = new BehaviorSubject<MmModel<Company[]>>((new MmModel<Company[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private plansSubject = new BehaviorSubject<MmModel<PlanData[]>>((new MmModel<PlanData[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private projectSettingsSubject = new BehaviorSubject<MmModel<ProjectSetting[]>>((new MmModel<ProjectSetting[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private projectUserPermssionsSubject = new BehaviorSubject<MmModel<ProjectUserPermissions>>((new MmModel<ProjectUserPermissions>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private attributesSubject = new BehaviorSubject<MmModel<Attribute[]>>((new MmModel<Attribute[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private attributeGroupsSubject = new BehaviorSubject<MmModel<AttributeGroup[]>>((new MmModel<AttributeGroup[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private attributeGroupsFilesSubject = new BehaviorSubject<MmModel<AttributeGroupFile[]>>((new MmModel<AttributeGroupFile[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));
  private delaysSubject = new BehaviorSubject<MmModel<Delay[]>>((new MmModel<Delay[]>({
    loadState: new LoadState({
      data: null,
      status: LoadStatusEnum.Loading
    })
  })));

  public project = this.projectSubject.asObservable();
  public fields = this.filedsSubject.asObservable();
  public layouts = this.layoutsSubject.asObservable();
  public structures = this.structuresSubject.asObservable();
  public orders = this.ordersSubject.asObservable();
  public crafts = this.craftsSubject.asObservable();
  public contacts = this.contactsSubject.asObservable();
  public userDefineds = this.userDefinedsSubject.asObservable();
  public classifications = this.classificationsSubject.asObservable();
  public statuses = this.statusesSubject.asObservable();
  public documentTypes = this.documentTypesSubject.asObservable();
  public companies = this.companiesSubject.asObservable();
  public plans = this.plansSubject.asObservable();
  public projectSettings = this.projectSettingsSubject.asObservable();
  public projectUserPermissions = this.projectUserPermssionsSubject.asObservable();
  public attributes = this.attributesSubject.asObservable();
  public attributeGroups = this.attributeGroupsSubject.asObservable();
  public attributeGroupsFiles = this.attributeGroupsFilesSubject.asObservable();
  public delays = this.delaysSubject.asObservable();

  constructor(private readonly connectivityService: ConnectivityService,
              private readonly offlineService: MmProjectOfflineService,
              private readonly notificationService: NotificationService) {

  }

  ngOnDestroy(): void {

  }

  private onStartLoading() {
    this.projectSubject.next(new MmModel<MmProject>({
      loadState: new LoadState({
        data: new MmProject(),
        status: LoadStatusEnum.Loading
      })
    }));
    this.layoutsSubject.next(new MmModel<Layout[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.filedsSubject.next(new MmModel<FieldMinimal[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.structuresSubject.next(new MmModel<Structure[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.ordersSubject.next(new MmModel<Order[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.craftsSubject.next(new MmModel<Craft[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.contactsSubject.next(new MmModel<Contact[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.userDefinedsSubject.next(new MmModel<UserDefineds>({
      loadState: new LoadState({
        data: new UserDefineds(),
        status: LoadStatusEnum.Loading
      })
    }));
    this.classificationsSubject.next(new MmModel<Classifications>({
      loadState: new LoadState({
        data: new Classifications(),
        status: LoadStatusEnum.Loading
      })
    }));
    this.statusesSubject.next(new MmModel<Status[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.documentTypesSubject.next(new MmModel<DocumentType[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.companiesSubject.next(new MmModel<Company[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.plansSubject.next(new MmModel<PlanData[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.documentTypesSubject.next(new MmModel<DocumentType[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.companiesSubject.next(new MmModel<Company[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.projectSettingsSubject.next(new MmModel<ProjectSetting[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.projectUserPermssionsSubject.next(new MmModel<ProjectUserPermissions>({
      loadState: new LoadState({
        data: new ProjectUserPermissions(),
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributesSubject.next(new MmModel<Attribute[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributeGroupsSubject.next(new MmModel<AttributeGroup[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributeGroupsFilesSubject.next(new MmModel<AttributeGroupFile[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.delaysSubject.next(new MmModel<Delay[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
  }

  private reset() {
    this.projectSubject.next(new MmModel<MmProject>({
      loadState: new LoadState({
        data: null,
        status: LoadStatusEnum.Loading
      })
    }));
    this.layoutsSubject.next(new MmModel<Layout[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.filedsSubject.next(new MmModel<FieldMinimal[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.structuresSubject.next(new MmModel<Structure[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.ordersSubject.next(new MmModel<Order[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.craftsSubject.next(new MmModel<Craft[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.contactsSubject.next(new MmModel<Contact[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.userDefinedsSubject.next(new MmModel<UserDefineds>({
      loadState: new LoadState({
        data: new UserDefineds(),
        status: LoadStatusEnum.Loading
      })
    }));
    this.classificationsSubject.next(new MmModel<Classifications>({
      loadState: new LoadState({
        data: new Classifications(),
        status: LoadStatusEnum.Loading
      })
    }));
    this.statusesSubject.next(new MmModel<Status[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.documentTypesSubject.next(new MmModel<DocumentType[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.companiesSubject.next(new MmModel<Company[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.plansSubject.next(new MmModel<PlanData[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }));
    this.documentTypesSubject.next(new MmModel<DocumentType[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.projectSettingsSubject.next(new MmModel<ProjectSetting[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.projectUserPermssionsSubject.next(new MmModel<ProjectUserPermissions>({
      loadState: new LoadState({
        data: new ProjectUserPermissions(),
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributesSubject.next(new MmModel<Attribute[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributeGroupsSubject.next(new MmModel<AttributeGroup[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.attributeGroupsFilesSubject.next(new MmModel<AttributeGroupFile[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
    this.delaysSubject.next(new MmModel<Delay[]>({
      loadState: new LoadState({
        data: [],
        status: LoadStatusEnum.Loading
      })
    }))
  }

  public loadProject(mydocmaProject: MydocmaProject): Observable<MmProject> {

    return defer(async () => {

      this.reset();
      if (!mydocmaProject) {
        return null;
      }
      console.debug('MmProjectLoaderService: Loading project ... Project: ' + mydocmaProject.id);
      this.onStartLoading();

      let project: MmProject;
      try {
        project = await firstValueFrom(this.offlineService.getProject(mydocmaProject.id, false));
        let settings: ProjectSetting[];
        if (!project && !this.connectivityService.isOfflineVal) {
          project = await firstValueFrom(this.offlineService.getProject(mydocmaProject.id, true));
          settings = await firstValueFrom(this.loadProjectSettings(project, true));
        } else if (project) {
          settings = await firstValueFrom(this.loadProjectSettings(project, false));
        }

        if (project) {
          this.checkBpConnection(project, settings)
          this.projectSubject.value.loadState = new LoadState({data: project, status: LoadStatusEnum.Loaded});
        } else {
          this.projectSubject.value.loadState = new LoadState({status: LoadStatusEnum.Error});
          this.projectSubject.value.loadState.error = `Project "${mydocmaProject.name} - ${mydocmaProject.desc}" not found in mm`;
          this.notificationService.error(this.projectSubject.value.loadState.error);
        }

      } catch (err) {
        console.error(err);
        this.projectSubject.value.loadState = new LoadState({status: LoadStatusEnum.Error});
        this.projectSubject.value.loadState.error = `Could not load mm project "${mydocmaProject.name} - ${mydocmaProject.desc}"`;
        this.notificationService.error(this.projectSubject.value.loadState.error);
      }

      if (this.projectSubject.value.loadState.status !== LoadStatusEnum.Loaded) {
        this.projectSubject.next(this.projectSubject.value);
        return project;
      }


      // Load persisted values from db
      await Promise.all([
        firstValueFrom(this.loadStructures(project, false)
          .pipe(switchMap(loadedStructures => this.structures.pipe(filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
            map(m => flattenStructure(m.data)))
            .pipe(switchMap(structures => this.loadPlans(project, structures, false)
            ))
          ))),
        firstValueFrom(this.loadLayouts(project, false)),
        firstValueFrom(this.loadFields(project, false)),
        firstValueFrom(this.loadOrders(project, false)),
        firstValueFrom(this.loadCrafts(project, false)),
        firstValueFrom(this.loadContacts(project, false)),
        firstValueFrom(this.loadUserDefineds(project, false)),
        firstValueFrom(this.loadClassifications(project, false)),
        firstValueFrom(this.loadStatuses(project, false)),
        firstValueFrom(this.loadDocumentTypes(project, false)),
        firstValueFrom(this.loadCompanies(project, false)),
        firstValueFrom(this.loadProjectSettings(project, false)),
        firstValueFrom(this.loadProjectUserPermssions(project, false)),
        firstValueFrom(this.loadAttributes(project, false)),
        firstValueFrom(this.loadAttributeGroups(project, false)),
        firstValueFrom(this.loadDelays(project, false))
      ]);

      console.debug('MmProjectLoaderService: Project loaded. Project: ' + mydocmaProject.id);
      this.projectSubject.next(this.projectSubject.value);

      //setTimeout(() => {
      //Start loading values from server if online
      if (!this.connectivityService.isOfflineVal) {

        Promise.all([
          firstValueFrom(this.loadStructures(project, true)
            .pipe(switchMap(loadedStructures => this.structures.pipe(filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
              map(m => flattenStructure(m.data)))
              .pipe(switchMap(structures => this.loadPlans(project, structures, true)
              ))
            ))),
          firstValueFrom(this.loadLayouts(project, true)),
          firstValueFrom(this.loadFields(project, true)),
          firstValueFrom(this.loadOrders(project, true)),
          firstValueFrom(this.loadCrafts(project, true)),
          firstValueFrom(this.loadContacts(project, true)),
          firstValueFrom(this.loadUserDefineds(project, true)),
          firstValueFrom(this.loadClassifications(project, true)),
          firstValueFrom(this.loadStatuses(project, true)),
          firstValueFrom(this.loadDocumentTypes(project, true)),
          firstValueFrom(this.loadCompanies(project, true)),
          firstValueFrom(this.loadProjectSettings(project, true)),
          firstValueFrom(this.loadProjectUserPermssions(project, true)),
          firstValueFrom(this.loadAttributes(project, true)),
          firstValueFrom(this.loadAttributeGroups(project, true)),
          firstValueFrom(this.loadDelays(project, true))
        ]);

        this.loadFieldConfig(project)
      }
      // }, 2000);


      return project;
    });

  }


  private checkBpConnection(project: MmProject, settings: ProjectSetting[]) {
    const planSource = settings.find(s => s.Key === ProjectSettingEnum.PlanSource);
    const textPinFieldSettings = settings.find(value => value.Key === 'SelectedPlanField' && value?.Setting);
    project.BPConnected = ((planSource && planSource.Setting === 'BP') ?? false);
    project.textPinEnabled = settings.some(value => value.Key === 'IsPlanPinsEnabled' && value.Setting === 'True');
    project.textPinFieldId = textPinFieldSettings?.Setting;
  }

  private loadClassifications(project: MmProject, forceRefresh: boolean): Observable<Classifications> {
    return this.offlineService.getClassifications(project, forceRefresh)
      .pipe(
        tap(uds => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.classificationsSubject.next(
              new MmModel<Classifications>({loadState: new LoadState({data: uds, status: LoadStatusEnum.Loaded})})
            );
          }
        })
      );
  }

  private loadUserDefineds(project: MmProject, forceRefresh: boolean): Observable<UserDefineds> {
    return this.offlineService.getUserDefineds(project, forceRefresh)
      .pipe(
        tap(uds => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.userDefinedsSubject.next(new MmModel<UserDefineds>({
              loadState: new LoadState({
                data: uds,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadContacts(project: MmProject, forceRefresh: boolean): Observable<Craft[]> {
    return this.offlineService.getContacts(project, forceRefresh)
      .pipe(
        tap(cs => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.contactsSubject.next(new MmModel<Contact[]>({
              loadState: new LoadState({
                data: cs,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadCrafts(project: MmProject, forceRefresh: boolean): Observable<Craft[]> {
    return this.offlineService.getCrafts(project, forceRefresh)
      .pipe(
        tap(cs => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.craftsSubject.next(new MmModel<Craft[]>({
              loadState: new LoadState({
                data: cs,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadStatuses(project: MmProject, forceRefresh: boolean): Observable<Status[]> {
    return this.offlineService.getStatuses(project, forceRefresh)
      .pipe(
        tap(ss => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.statusesSubject.next(new MmModel<Status[]>({
              loadState: new LoadState({
                data: ss,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadDocumentTypes(project: MmProject, forceRefresh: boolean): Observable<DocumentType[]> {
    return this.offlineService.getDocumentTypes(project, forceRefresh)
      .pipe(
        tap(ss => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.documentTypesSubject.next(new MmModel<DocumentType[]>({
              loadState: new LoadState({
                data: ss,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadCompanies(project: MmProject, forceRefresh: boolean): Observable<Company[]> {
    return this.offlineService.getCompanies(project, forceRefresh)
      .pipe(
        tap(ss => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.companiesSubject.next(new MmModel<Company[]>({
              loadState: new LoadState({
                data: ss,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }


  private loadOrders(project: MmProject, forceRefresh: boolean): Observable<Layout[]> {
    return this.offlineService.getOrders(project, forceRefresh)
      .pipe(
        tap(os => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.ordersSubject.next(new MmModel<Layout[]>({
              loadState: new LoadState({
                data: os,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadLayouts(project: MmProject, forceRefresh: boolean): Observable<Layout[]> {
    return this.offlineService.getLayouts(project, forceRefresh)
      .pipe(
        tap(ls => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.layoutsSubject.next(new MmModel<Layout[]>({
              loadState: new LoadState({
                data: ls,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  loadLayoutsByAddedOrUpdated(project: MmProject, layout: Layout, forceRefresh: boolean): Observable<Layout[]> {
    return this.offlineService.getLayoutsByAddedOrUpdated(project, layout, forceRefresh)
      .pipe(
        tap(ls => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.layoutsSubject.next(new MmModel<Layout[]>({
              loadState: new LoadState({
                data: ls,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  loadLayoutsByDeleted(project: MmProject, layoutId: number, forceRefresh: boolean): Observable<Layout[]> {
    return this.offlineService.getLayoutsByDeleted(project, layoutId, forceRefresh)
      .pipe(
        tap(ls => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.layoutsSubject.next(new MmModel<Layout[]>({
              loadState: new LoadState({
                data: ls,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadFields(project: MmProject, forceRefresh: boolean): Observable<FieldMinimal[]> {
    return this.offlineService.getFields(project, forceRefresh)
      .pipe(
        tap(fs => {
          if (this.projectSubject.value.data.Id === project.Id && (fs?.length ?? 0) > 0) {
            this.filedsSubject.next(new MmModel<FieldMinimal[]>({
              loadState: new LoadState({
                data: fs,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadStructures(project: MmProject, forceRefresh: boolean): Observable<Structure[]> {
    return this.offlineService.getStructures(project, forceRefresh)
      .pipe(
        tap(ss => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.structuresSubject.next(new MmModel<Structure[]>({
              loadState: new LoadState({
                data: ss,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }


  private loadPlans(project: MmProject, structures: Structure[], forceRefresh: boolean): Observable<PlanData[]> {
    return this.offlineService.getPlans(project, structures, forceRefresh)
      .pipe(
        tap(ps => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.plansSubject.next(new MmModel<PlanData[]>({
              loadState: new LoadState({
                data: ps,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadProjectSettings(project: MmProject, forceRefresh: boolean): Observable<ProjectSetting[]> {
    return this.offlineService.getProjectSettings(project, forceRefresh)
      .pipe(
        tap(ps => {
          if (this.projectSubject.value.data.Id == project.Id) {
            this.checkBpConnection(project, ps);
            this.projectSettingsSubject.next(new MmModel<ProjectSetting[]>({
              loadState: new LoadState({
                data: ps,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadProjectUserPermssions(project: MmProject, forceRefresh: boolean) {
    return this.offlineService.getPermissions(project, forceRefresh)
      .pipe(
        tap(pup => {
          if (this.projectSubject.value.data.Id == project.Id) {
            this.projectUserPermssionsSubject.next(new MmModel({
              loadState: new LoadState({
                data: pup,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadFieldConfig(project: MmProject): void {
    //  this.fieldConfigApiService.loadFieldConfig(project);
  }

  private loadAttributes(project: MmProject, forceRefresh: boolean): Observable<Attribute[]> {
    return this.offlineService.getAttributes(project, forceRefresh)
      .pipe(
        tap(data => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.attributesSubject.next(new MmModel<Status[]>({
              loadState: new LoadState({
                data: data,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadAttributeGroups(project: MmProject, forceRefresh: boolean): Observable<{
    attributeGroup: AttributeGroup[],
    attributeGroupFiles: AttributeGroupFile[]
  }> {
    return this.offlineService.getAttributeGroups(project, forceRefresh)
      .pipe(
        tap(data => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.attributeGroupsSubject.next(new MmModel<AttributeGroup[]>({
              loadState: new LoadState({
                data: data.attributeGroup,
                status: LoadStatusEnum.Loaded
              })
            }));
            this.attributeGroupsFilesSubject.next(new MmModel<AttributeGroupFile[]>({
              loadState: new LoadState({
                data: data.attributeGroupFiles,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  private loadDelays(project: MmProject, forceRefresh: boolean): Observable<Delay[]> {
    return this.offlineService.getDelays(project, forceRefresh)
      .pipe(
        tap(data => {
          if (this.projectSubject.value.data.Id === project.Id) {
            this.delaysSubject.next(new MmModel<Delay[]>({
              loadState: new LoadState({
                data: data,
                status: LoadStatusEnum.Loaded
              })
            }));
          }
        })
      );
  }

  public updateCompaniesSubject(data: Company[]) {
    this.companiesSubject.next(new MmModel<Company[]>({
      loadState: new LoadState({
        data: data,
        status: LoadStatusEnum.Loaded
      })
    }));
  }

  public updateContactsSubject(data: Contact[]) {
    this.contactsSubject.next(new MmModel<Contact[]>({
      loadState: new LoadState({
        data: data,
        status: LoadStatusEnum.Loaded
      })
    }));
  }

  public updateContractsSubject(data: Order[]) {
    this.ordersSubject.next(new MmModel<Order[]>({
      loadState: new LoadState({
        data: data,
        status: LoadStatusEnum.Loaded
      })
    }));
  }

  public updateCraftsSubject(data: Craft[]) {
    this.craftsSubject.next(new MmModel<Craft[]>({
      loadState: new LoadState({
        data: data,
        status: LoadStatusEnum.Loaded
      })
    }));
  }

}
